import { useCallback, useEffect, useMemo, useState } from "react";
import { ElasticProduct } from "@usereactify/search";
import axios from "axios";

import type { Cart, GiftProduct, GQLProduct, NormalisedProduct, Product, LineItem } from "@app/scripts/types";
import { formatPrice, updateCart, addToBackInStock, addToCart, stripColourFromVariantTitle } from "@app/scripts/utils";
import { shopify } from "@app/scripts/utils/graphql";
import { GET_PRODUCTS_BY_ID } from "@app/scripts/utils/queries";
import config from "@app/config";

export const useShopifyCart = ({ giftProduct }: { giftProduct?: GiftProduct }) => {
  const { normaliseProduct } = useShopify();
  const [products, setProducts] = useState<NormalisedProduct[]>([]);
  const [cart, setCart] = useState<Cart | null>(null);
  const [oosItems, setOosItems] = useState<number[]>([]); // array of oos variant ids
  const [error, setError] = useState<string | undefined | null>(null);

  const forceGlobalUpdateEvent = new Event("forceGlobalUpdate", { bubbles: true });

  // Gets gift data and normalises it
  const gifts = useMemo(
    () => giftProduct?.gift_products?.map((product) => normaliseProduct(product)),
    [normaliseProduct, giftProduct?.gift_products]
  );

  // Checks if gifts has data and maps IDs into an array
  const giftIds = useMemo(() => (gifts?.length && gifts?.map((gift) => gift.id)) || [], []);

  // Filters through cart data to see if there are gifts in the cart
  const giftsInCart = useMemo(
    () => cart?.items?.filter((lineItem) => giftIds?.includes(lineItem.product_id) && lineItem.properties?.isGWP === "true") || [],
    [cart?.items, config?.attributes?.isGwp]
  );

  // If theres gifts in the cart, checks to see how much they cost
  const totalGiftPrice = useMemo(
    () =>
      giftsInCart?.reduce((acc: number, giftLine: LineItem) => {
        return (acc += giftLine.final_price / 100);
      }, 0),
    [giftsInCart]
  );

  // Creates cart total based on subtotal and if there are a gift in the cart
  const cartTotal = useMemo(() => {
    if (!cart) return 0;
    const subtotal = cart.items_subtotal_price / 100;
    return totalGiftPrice > 0 ? subtotal - totalGiftPrice : subtotal;
  }, [cart?.items_subtotal_price, totalGiftPrice]);

  // Checks to see if customers cart is over the gift threshold
  const overGiftThreshold = useMemo(() => {
    return !!(giftProduct?.gift_threshold && cartTotal > giftProduct.gift_threshold);
  }, [giftProduct, cartTotal]);

  const untilGiftThreshold = giftProduct?.gift_threshold - cartTotal || 0;

  const removeOldGift = useCallback(
    async (lastGiftId: number | null) => {
      // search for the last gift and remove it as it's been switched off
      if (!lastGiftId) return;

      const oldGift = cart?.items?.find((i) => i.product_id === lastGiftId);
      if (!oldGift) return;

      await handleUpdate(oldGift.id, 0);
      localStorage.removeItem(`last_gift_id`);
    },
    [cart?.items]
  );

  // Handles GWP add/remove when singular product is offered
  const addRemoveGift = useCallback(async () => {
    const lastGiftId = localStorage.getItem(`last_gift_id`) ? parseInt(localStorage.getItem(`last_gift_id`)) : null;

    if (!gifts) {
      await removeOldGift(lastGiftId);
      return;
    }
    const firstGiftVariant = gifts[0].variants?.find((v) => v.available);
    const manuallyRemoved = localStorage.getItem(`gift_removed:${gifts[0].id}`) === "true";

    if (!giftProduct?.gift_threshold || !firstGiftVariant || manuallyRemoved) return;
    if (lastGiftId && gifts[0].id !== lastGiftId) {
      await removeOldGift(lastGiftId);
    }

    const overGiftThreshold = cartTotal > giftProduct.gift_threshold;

    if (giftsInCart.length == 0 && overGiftThreshold && firstGiftVariant) {
      await addToCart({
        id: firstGiftVariant.id,
        qty: 1,
        properties: { [config.attributes.isGwp]: "true" }
      });
      // store the last gift id added so if its removed from the customiser it can be removed automatically from the cart
      localStorage.setItem(`last_gift_id`, gifts[0].id.toString());
    } else if (giftsInCart && !overGiftThreshold && firstGiftVariant) {
      await handleUpdate(firstGiftVariant.id, 0);
    }
  }, [gifts, cart, giftsInCart, giftProduct?.gift_threshold, cartTotal, removeOldGift]);

  // Handles removal of GWP product when multiple gifts are offered
  const handleRemoveGiftFromCart = useCallback(async () => {
    const giftVariant = cart?.items?.filter((lineItem) => giftIds?.includes(lineItem.product_id))
    if (!giftVariant) return;

    await handleUpdate(giftVariant[0]?.id, 0);
  }, [gifts]);

  const totalDiscount = useMemo(() => {
    let td = 0;
    cart?.items?.forEach((item) => {
      const matchingProduct = products.find((p) => p.id === item.product_id);
      const matchingVariant = matchingProduct?.variants?.find((v) => v.id === item.variant_id);
      if (matchingVariant)
        td += matchingVariant?.compare_at_price
          ? (matchingVariant.compare_at_price - matchingVariant.price) * item.quantity
          : 0;
    });
    return td > 0 ? formatPrice(td) : null;
  }, [cart?.items, products]);

  const cartBeforeDiscountPrice = useMemo(() => {
    let td = 0;
    cart?.items?.forEach((item) => {
      const matchingProduct = products.find((p) => p.id === item.product_id);
      const matchingVariant = matchingProduct?.variants?.find((v) => v.id === item.variant_id);
      if (matchingVariant)
        td += matchingVariant?.compare_at_price
          ? (matchingVariant.compare_at_price - matchingVariant.price) * item.quantity
          : 0;
    });
    return formatPrice(cartTotal + td);
  }, [cart?.items, products]);

  const addOosVariant = useCallback(
    (variantId: number) => {
      if (!oosItems?.includes(variantId)) {
        setOosItems([...(oosItems || []), variantId]);
      }
    },
    [setOosItems, oosItems]
  );

  const fetchCart = useCallback(async () => {
    try {
      const cart = (await axios.get("/cart.json"))?.data
      const products = await shopify(GET_PRODUCTS_BY_ID, {
        ids: cart?.items?.map((item: any) => `gid://shopify/Product/${item?.product_id}`),
      })
      const normalisedProducts = products?.nodes?.map((product: Product) => {
        const result = normaliseProduct(product)
        return {
          ...result,
          id: Number(result?.id?.split("/")[4]),
          variants: result?.variants?.map((v: any) => ({
            ...v,
            id: Number(v?.id?.split("/")[4])
          }))
        }
      })
      setCart(cart)
      setProducts(normalisedProducts)
    } catch (err) {
      console.error(err)
    }
  }, [setCart, setProducts, normaliseProduct]);

  const handleUpdate = useCallback(
    async (variantId: number, qty: number, refresh = true) => {
      await updateCart({ id: variantId, qty: qty });
      if (refresh) {
        window.dispatchEvent(forceGlobalUpdateEvent);
      }
    },
    [fetchCart, updateCart, addRemoveGift]
  );

  const goToCheckout = useCallback(
    async (e: any) => {
      e.preventDefault?.();

      if (cart?.items) {
        for (const item of cart?.items) {
          if (oosItems.includes(item.variant_id)) {
            await updateCart({ id: item.variant_id, qty: 0 });
          }
        }
      }
      window.location.href = "/checkout";
    },
    [cart?.items, oosItems, updateCart]
  );

  const onTabVisible = useCallback(async () => {
    if (document.visibilityState == "visible") {
      fetchCart();
    }
  }, [fetchCart, cart]);


  // handles removal of gifts if the user goes under the threshold (only when multiple gifts are offered)
  useEffect(() => {
    if (giftIds?.length > 1) {
      if (giftsInCart?.length > 0 && !overGiftThreshold) {
        handleRemoveGiftFromCart();
      }
    }
  }, [cart, giftsInCart, overGiftThreshold, giftIds]);

  useEffect(() => {
    if (giftIds?.length == 1) {
      if (giftsInCart?.length > 0 || overGiftThreshold) {
        addRemoveGift();
      }
    }
  }, [cart, giftsInCart, overGiftThreshold, giftIds]);

  useEffect(() => {
    window.addEventListener("forceGlobalUpdate", fetchCart);
    document.addEventListener("visibilitychange", onTabVisible);

    return () => {
      window.removeEventListener("forceGlobalUpdate", fetchCart);
      document.removeEventListener("visibilitychange", onTabVisible);
    };
  }, []);

  return {
    products,
    cart,
    fetchCart,
    goToCheckout,
    handleUpdate,
    totalDiscount,
    addOosVariant,
    cartTotal,
    oosItems,
    giftIds,
    giftMessage: giftProduct?.gift_message,
    cartBeforeDiscountPrice,
    error,
    setError,
    gifts,
    giftsInCart,
    overGiftThreshold,
    untilGiftThreshold,
  };
};

export const useShopifyProduct = ({ product }: { product?: NormalisedProduct }) => {
  const [error, setError] = useState<string | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState(product?.variants?.[0]?.id);

  const title = product?.title?.substr(0, product?.title.lastIndexOf("-"));

  const getColor = (p?: NormalisedProduct) => {
    const arr = p?.title?.split("-");
    return arr?.length ? arr.slice(-1) : null;
  };

  const getColorFromTitle = (p?: NormalisedProduct) => {
    if (!p) return "";

    return p.title.split(" - ")[1] || "";
  };

  const getBadges = (p?: NormalisedProduct) =>
    p?.tags
      ?.filter((tag) => tag.includes("flag--"))
      ?.map((tag) => ({
        tag: tag.toLowerCase(),
        title: tag.replace("flag--", ""),
      }));

  const colorFromTitle = useMemo(() => getColorFromTitle(product), [product?.title, getColorFromTitle]);

  const color = useMemo(() => getColor(product), [product?.tags, getColor]);

  const badges = useMemo(() => getBadges(product), [product?.tags, getBadges]);

  const url = `/products/${product?.handle}`;

  const handleBISSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.target);
    const email = data.get("email") as string;
    const variantId = parseInt(data?.get("variant") as string);
    const res = await addToBackInStock({ email, variantId });
  };

  const handleChangeVariant = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
      setError(null);
      setSelectedVariantId(e.target.value);
      return e.target.value;
    },
    [setSelectedVariantId, product?.variants, setError, selectedVariantId]
  );

  const decodeShopifyId = (id: string | number, type: string) => {
    return id?.split?.(`${type}/`)?.[1] || id;
  };

  const handleAddToCart = useCallback(
    async (
      e: React.FormEvent<HTMLFormElement>,
      variant_id: any | null = null,
      properties: { [key: string]: string } | null = null
    ) => {
      e.preventDefault();
      setError(null);

      const variantId = Number(decodeShopifyId(selectedVariantId, "Variant")) || Number(variant_id);

      const res = await addToCart({
        id: variantId,
        qty: 1,
        properties: properties,
      });

      if (res.status === 200) {
        return true;
      } else {
        setError((res?.description as string) ?? "There was an error adding to the cart");
        return false;
      }
    },
    [addToCart, selectedVariantId, setError]
  );

  return {
    handleAddToCart,
    setSelectedVariantId,
    handleChangeVariant,
    selectedVariantId,
    handleBISSubmit,
    getColor,
    color,
    getColorFromTitle,
    colorFromTitle,
    getBadges,
    badges,
    error,
    url,
    setError,
    title,
  };
};

export const useShopify = () => {
  const normaliseElasticProduct: (product: ElasticProduct) => NormalisedProduct = (product) => ({
    is_normalised: true,
    available: true,
    compare_at_price: product.discount_amount ? product.price_min + product.discount_amount : null,
    compare_at_price_min: product.discount_amount ? product.price_min + product.discount_amount : null,
    compare_at_price_max: product.discount_amount ? product.price_max + product.discount_amount : null,
    description: product.description,
    featured_image: {
      id: product.images?.[0]?.src,
      src: product.images?.[0]?.src,
      alt: product.images?.[0]?.alt,
    },
    handle: product.handle,
    id: product.id.toString(),
    media: product.images?.map((img) => ({
      id: img?.src,
      src: img?.src,
      alt: img?.alt,
    })),
    price: product.price_min,
    price_min: product.price_min,
    price_max: product.price_max,
    tags: product.tags,
    title: product.title,
    variants: product.variants?.map((variant) => ({
      title: stripColourFromVariantTitle(variant.title),
      available: variant.available,
      compare_at_price: variant.compare_at_price,
      currentlyNotInStock: variant.inventory_quantity === 0 && variant.available === false,
      id: variant.id,
      price: variant.price,
    })),
  });

  const normaliseProduct: (product: Product | GQLProduct) => NormalisedProduct = (product) => {
    const isShopifyJson = !!(product as Product)?.price;

    if (isShopifyJson) {
      return {
        ...product,
        is_normalised: true,
        price: (product as Product).price / 100,
        compare_at_price: (product as Product)?.compare_at_price ? (product as Product).compare_at_price / 100 : null,
        media: (product as Product)?.media?.map((m) => ({
          alt: m.alt,
          height: m.height,
          width: m.width,
          src: m.src,
          id: m.id,
        })),
        variants: (product as Product).variants?.map((variant) => ({
          available: variant.available,
          compare_at_price: variant.compare_at_price ? variant.compare_at_price / 100 : null,
          currentlyNotInStock: "check_via_gql",
          id: variant.id,
          price: variant.price / 100,
          title: stripColourFromVariantTitle(variant.title),
        })),
      };
    }

    return {
      is_normalised: true,
      available: (product as GQLProduct)?.availableForSale,
      compare_at_price: parseFloat((product as GQLProduct)?.compareAtPriceRange?.minVariantPrice.amount),
      compare_at_price_min: parseFloat((product as GQLProduct)?.compareAtPriceRange?.minVariantPrice.amount),
      compare_at_price_max: parseFloat((product as GQLProduct)?.compareAtPriceRange?.maxVariantPrice.amount),
      description: (product as GQLProduct)?.descriptionHtml,
      productType: (product as GQLProduct)?.productType,
      featured_image: {
        alt: (product as GQLProduct)?.images?.edges?.[0]?.node.altText,
        height: (product as GQLProduct)?.images?.edges?.[0]?.node.height,
        width: (product as GQLProduct)?.images?.edges?.[0]?.node.width,
        src: (product as GQLProduct)?.images?.edges?.[0]?.node.url,
        id: (product as GQLProduct)?.images?.edges?.[0]?.node.id,
      },
      handle: (product as GQLProduct)?.handle,
      id: (product as GQLProduct)?.id,
      media: (product as GQLProduct)?.images.edges.map(({ node }) => ({
        alt: node.altText,
        height: node.height,
        width: node.width,
        src: node.url,
        id: node.id,
      })),
      price: parseFloat((product as GQLProduct)?.priceRange.minVariantPrice.amount),
      price_min: parseFloat((product as GQLProduct)?.priceRange.minVariantPrice.amount),
      price_max: parseFloat((product as GQLProduct)?.priceRange.maxVariantPrice.amount),
      tags: (product as GQLProduct)?.tags,
      title: (product as GQLProduct)?.title,
      variants: (product as GQLProduct)?.variants?.edges?.map(({ node }) => ({
        title: stripColourFromVariantTitle(node.title),
        available: node.availableForSale,
        compare_at_price: node.compareAtPriceV2?.amount ? parseFloat(node.compareAtPriceV2.amount) : null,
        currentlyNotInStock: node.currentlyNotInStock,
        id: node.id,
        price: parseFloat(node.priceV2.amount),
      })),
    };
  };

  return {
    normaliseProduct,
    normaliseElasticProduct,
  };
};
